import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, MinusCircle, Loader2, Scale, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const brDate = (s) => s ? new Date(s.slice(0,10) + "T00:00:00").toLocaleDateString("pt-BR") : "—";

export default function KanbanPrazos() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const r = await api.get("/djen/kanban-prazos", { params: { days: d } });
      setData(r.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const cols = [
    { key: "CALCULATED",       title: "CONFIRMADOS",       icon: CheckCircle2,
      color: "emerald", desc: "Regra + dois validadores concordam" },
    { key: "REVIEW_REQUIRED",  title: "REVISÃO NECESSÁRIA", icon: AlertTriangle,
      color: "amber",   desc: "Regra ausente, validadores divergem ou dados incompletos" },
    { key: "NO_DEADLINE",      title: "SEM PRAZO",           icon: MinusCircle,
      color: "slate",   desc: "IA classificou como sem prazo aplicável" },
  ];

  return (
    <div className="flex-1 min-w-0 flex flex-col p-6 lg:p-8 font-sans max-w-7xl mx-auto w-full space-y-6" data-testid="kanban-prazos">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to="/djen" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-2">
            <ArrowLeft className="w-3 h-3"/> DJEN
          </Link>
          <h1 className="font-heading text-3xl font-semibold text-slate-900 flex items-center gap-2">
            <Scale className="w-7 h-7 text-rose-600"/> Prazos Hermes
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Prazos derivados das publicações do DJEN pelo motor v2, com regras
            versionadas e segundo validador. <strong>CONFIRMADOS</strong> só entram na coluna
            verde quando os dois algoritmos independentes concordam.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={e => { setDays(+e.target.value); load(+e.target.value); }}
            className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm"
            data-testid="kanban-days">
            <option value="7">Últimos 7 dias</option>
            <option value="15">Últimos 15 dias</option>
            <option value="30">Últimos 30 dias</option>
            <option value="60">Últimos 60 dias</option>
            <option value="90">Últimos 90 dias</option>
          </select>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin"/> Reprocessando com engine v2…
        </div>
      )}

      {!loading && data && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {cols.map(col => {
            const items = data.columns?.[col.key] || [];
            const Icon = col.icon;
            const c = col.color;
            const borderCls = {emerald:"border-emerald-200",amber:"border-amber-200",slate:"border-slate-200"}[c];
            const bgCls = {emerald:"bg-emerald-50",amber:"bg-amber-50",slate:"bg-slate-50"}[c];
            const iconCls = {emerald:"text-emerald-700",amber:"text-amber-700",slate:"text-slate-500"}[c];
            const badgeCls = {emerald:"bg-emerald-600 text-white",amber:"bg-amber-500 text-white",slate:"bg-slate-500 text-white"}[c];
            return (
              <div key={col.key} className={`rounded-2xl border ${borderCls} ${bgCls} p-4 min-h-[400px]`}
                    data-testid={`kanban-col-${col.key}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon className={`w-5 h-5 ${iconCls}`}/>
                  <h2 className="font-semibold text-slate-900">{col.title}</h2>
                  <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${badgeCls}`}>
                    {items.length}
                  </span>
                </div>
                <div className="text-[11px] text-slate-500 mb-3">{col.desc}</div>
                <div className="space-y-2">
                  {items.map((it, i) => (
                    <div key={it.publication_id || i}
                          className="rounded-lg border border-white/60 bg-white p-3 shadow-sm">
                      <div className="text-xs font-mono text-slate-500">{it.numero_processo || "—"}</div>
                      <div className="text-sm text-slate-900 font-medium">{it.tribunal} · {it.tipo}</div>
                      {it.resumo && (
                        <div className="text-xs text-slate-600 mt-1 line-clamp-2">{it.resumo}</div>
                      )}
                      {it.calculation && (
                        <div className="mt-2 rounded-md bg-slate-50 p-2 text-xs">
                          <div className="flex justify-between">
                            <span className="text-slate-500">Publicação:</span>
                            <span className="font-mono">{brDate(it.calculation.publication_date)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Início contagem:</span>
                            <span className="font-mono">{brDate(it.calculation.count_start_date)}</span>
                          </div>
                          <div className="flex justify-between text-emerald-800 font-semibold">
                            <span>Vencimento:</span>
                            <span className="font-mono">{brDate(it.calculation.due_date)}</span>
                          </div>
                          {it.rule && (
                            <div className="mt-1 pt-1 border-t border-slate-200 text-[10px] text-slate-500">
                              {it.rule.rule_id} · {it.rule.duration} {it.rule.counting_mode === "BUSINESS_DAYS" ? "dias úteis" : "dias corridos"}
                            </div>
                          )}
                        </div>
                      )}
                      {!it.calculation && it.reason && (
                        <div className="mt-2 text-[11px] text-amber-800 italic">{it.reason}</div>
                      )}
                      {it.linked_client?.nome && (
                        <div className="mt-2 text-[11px] text-sky-700">
                          👤 {it.linked_client.nome}
                        </div>
                      )}
                      <div className="mt-1 text-[10px] text-slate-400 font-mono">
                        Disp: {brDate(it.data_disponibilizacao)}
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && (
                    <div className="text-xs text-slate-400 italic text-center py-6">
                      Nenhum item nesta coluna
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
